import { defineStore } from "pinia";

interface ContactLink {
  /** What the user sees. */
  name: string;
  /** Where it goes — a URL, or the bare value for tel:/mailto:. */
  link: string;
}

/** Shape ContactMap consumes: it reads location.lat / location.long as strings. */
interface Address {
  address: string;
  location: {
    lat: string;
    long: string;
  };
}

interface ContactInfo {
  phone?: ContactLink;
  telegram?: ContactLink;
  email?: ContactLink;
  youtube?: ContactLink;
  facebook?: ContactLink;
  instagram?: ContactLink;
  website?: ContactLink;
  addresses: Address[];
}

/**
 * The university's own contact details, and where its campus is.
 *
 * <p>These used to come from app.usat.uz. When that service went away the fetch was replaced with
 * `{ addresses: [] }`, which left the contacts card empty and the map showing a hardcoded
 * "Адрес не найден" over a generic view of Tashkent.
 *
 * <p>Every value below is taken from something authoritative rather than guessed: the address and
 * phone are the ones printed on the signed contract template, the social links are the ones the
 * university publishes on its own sign-up pages, and the coordinates are the campus pin from
 * Yandex Maps. They are constants here because they belong to the institution, not to the logged-in
 * student — there is no per-user variation for an API to return.
 */
const USAT_CONTACTS: ContactInfo = {
  phone: { name: "+998 78 888 38 88", link: "+998788883888" },
  telegram: { name: "@usatuzb", link: "https://t.me/usatuzb" },
  instagram: { name: "@usatuz", link: "https://instagram.com/usatuz" },
  facebook: { name: "usatuz", link: "https://facebook.com/usatuz" },
  website: { name: "usat.uz", link: "https://usat.uz/" },
  addresses: [
    {
      address: "Toshkent shahri, Chilonzor tumani, Diydor ko'chasi 71-uy",
      location: { lat: "41.260386", long: "69.163690" },
    },
  ],
};

export const useContactStore = defineStore("ContactStore", {
  state: () => ({
    contactData: null as ContactInfo | null,
    loading: false,
    error: null as string | null,
    userCoordinates: null as { longitude: number; latitude: number } | null,
  }),
  actions: {
    async fetchContactInfo() {
      if (this.contactData) return;
      this.loading = true;
      this.error = null;
      try {
        this.contactData = USAT_CONTACTS;
      } finally {
        this.loading = false;
      }
    },
    clearContactData() {
      this.contactData = null;
      this.error = null;
    },
    setUserCoordinates(coordinates: { longitude: number; latitude: number }) {
      this.userCoordinates = coordinates;
    },
  },
  getters: {
    locations: (state): Address[] => state.contactData?.addresses || [],
    hasContactData: (state) => !!state.contactData,
  },
});
